import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Card,
  CardContent,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  Tabs,
  Tab,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  Save as SaveIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  LocationOn as LocationIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Badge as BadgeIcon,
  PersonPin as PersonPinIcon,
  Settings as SettingsIcon,
  LockOutlined as LockOutlinedIcon,
} from '@mui/icons-material';
import { useMaterialUIController, useGlobalMDAlert } from 'context';
import { useApi } from 'lib/api';
import { tienePermisosModulo } from 'lib/seguridad/permisosPorModulo';
import { FP, PageHeader, fpInputSx } from 'components/financepro';
import ListadoClientesSuplidores from './ListadoClientesSuplidores';
import ConfiguracionClientes from './ConfiguracionClientes';

/** Columnas diálogo buscar clientes — anchos sólo en <colgroup> para alinear thead y tbody */
const BUSQUEDA_DIALOG_COLUMNAS = [
  { campo: 'id', etiqueta: 'ID', align: 'left', width: '8%' },
  { campo: 'descrip', etiqueta: 'Nombre', align: 'left', width: '36%' },
  { campo: 'rnc', etiqueta: 'RNC / Cédula', align: 'left', width: '18%' },
  { campo: 'ciudad', etiqueta: 'Ciudad', align: 'left', width: '22%' },
  { campo: '_accion', etiqueta: 'Acción', align: 'center', width: '16%' },
];

const busquedaDialogCeldaLayoutSx = {
  minWidth: 0,
  boxSizing: 'border-box',
  verticalAlign: 'middle',
  padding: '10px 16px',
};

const RegistroClientes = () => {
  const [controller] = useMaterialUIController();
  const { nombreNegocio, roleDetalles } = controller;
  const { showAlert } = useGlobalMDAlert();
  const { post } = useApi();
  const showSuccess = (message) =>
    showAlert({ message, color: 'success', autoHideDuration: 3000 });
  const showError = (message) =>
    showAlert({ message, color: 'error', autoHideDuration: 4000 });
  const selectedCompany = nombreNegocio;

  // Estados del formulario
  const [tipoClienteSuplidor, setTipoClienteSuplidor] = useState('Cliente');
  const [nombre, setNombre] = useState('');
  const [direccion, setDireccion] = useState('');
  const [ciudad, setCiudad] = useState('');
  const [telefono, setTelefono] = useState('');
  const [fax, setFax] = useState('');
  const [email, setEmail] = useState('');
  const [rnc, setRnc] = useState('');

  // Estados auxiliares
  const [ciudades, setCiudades] = useState([]);
  const [rncHabilitado, setRncHabilitado] = useState(true);
  const [modoEdicion, setModoEdicion] = useState(false);

  // Estados para búsqueda
  const [dialogoBusqueda, setDialogoBusqueda] = useState(false);
  const [resultadosBusqueda, setResultadosBusqueda] = useState([]);
  const [descripcionBusqueda, setDescripcionBusqueda] = useState('');
  const [tipoBusquedaDialogo, setTipoBusquedaDialogo] = useState('Cliente');
  const [buscando, setBuscando] = useState(false);
  const [tabActual, setTabActual] = useState('registro');
  const [clienteIdParaConfiguracion, setClienteIdParaConfiguracion] = useState(null);

  // Referencias para autofocus
  const nombreRef = useRef(null);

  // Lista de ciudades de República Dominicana (del código Python)
  const CIUDADES_DOMINICANAS = [
    "Santiago", "Azua", "Bahoruco", "Barahona", "Dajabon", "Distrito Nacional",
    "Duarte", "Elias Pina", "El Seybo", "Espaillat", "Hato Mayor", "Salcedo",
    "Independencia", "La Altagracia", "La Romana", "La Vega", "Maria Trinidad Sanchez",
    "Mosenor Nouel", "Montecristi", "Monteplata", "Pedernales", "Peravia",
    "Puerto Plata", "Samana", "San Cristobal", "San Jose de Ocoa", "San Juan",
    "Sanchez Ramirez", "Santiago Rodriguez", "Santo Domingo", "Valverde"
  ];

  useEffect(() => {
    setCiudades(CIUDADES_DOMINICANAS);
  }, []);

  const convertirAMayusculas = (texto) => texto.toUpperCase();

  const handleNombreChange = (e) => {
    setNombre(convertirAMayusculas(e.target.value));
  };

  const formatearTelefono = (valor) => {
    const numeros = valor.replace(/\D/g, '');
    if (numeros.length <= 3) return numeros;
    if (numeros.length <= 6) return `${numeros.slice(0, 3)}-${numeros.slice(3)}`;
    return `${numeros.slice(0, 3)}-${numeros.slice(3, 6)}-${numeros.slice(6, 10)}`;
  };

  const handleTelefonoChange = (e) => setTelefono(formatearTelefono(e.target.value));
  const handleFaxChange = (e) => setFax(formatearTelefono(e.target.value));

  const validarFormulario = () => {
    if (!nombre.trim()) {
      showError('El nombre es requerido');
      if (nombreRef.current) nombreRef.current.focus();
      return false;
    }
    if (!direccion.trim()) {
      showError('La dirección es requerida');
      return false;
    }
    if (!email.trim()) {
      showError('El email es requerido');
      return false;
    }
    if (!rnc.trim()) {
      showError('El RNC es requerido');
      return false;
    }
    return true;
  };

  const limpiarFormulario = () => {
    setNombre('');
    setDireccion('');
    setCiudad('');
    setTelefono('');
    setFax('');
    setEmail('');
    setRnc('');
    setRncHabilitado(true);
    setModoEdicion(false);
    setDescripcionBusqueda('');
    if (nombreRef.current) nombreRef.current.focus();
  };

  const handleGuardar = async () => {
    if (!validarFormulario()) return;

    try {
      const { ok, data } = await post('/api/clientesbackend_guardar', {
        id_empresa: selectedCompany,
        nombre,
        direccion,
        ciudad,
        telefono,
        fax,
        email,
        vendedor: '',
        rnc,
        limcredito: 0,
        limcompra: 0,
        desc1: 0,
        desc2: 0,
        desc3: 0,
        tipo: tipoClienteSuplidor,
        area: null,
      });

      if (ok) {
        showSuccess(data.mensaje || 'Datos guardados exitosamente');
        limpiarFormulario();
      } else {
        showError(data.Error || 'Error al guardar los datos');
      }
    } catch (error) {
      showError('Error de conexión al guardar');
    }
  };

  const handleModificar = async () => {
    if (!rnc.trim()) {
      showError('Debe buscar y seleccionar un cliente o suplidor para modificar');
      return;
    }
    if (!validarFormulario()) return;

    try {
      const { ok, data } = await post('/api/clientesbackend_modificar', {
        id_empresa: selectedCompany,
        nombre,
        direccion,
        ciudad,
        telefono,
        fax,
        email,
        vendedor: '',
        rnc,
        limcredito: 0,
        limcompra: 0,
        desc1: 0,
        desc2: 0,
        desc3: 0,
        tipo: tipoClienteSuplidor,
        area: null,
      });

      if (ok) {
        showSuccess(data.mensaje || 'Datos modificados exitosamente');
        limpiarFormulario();
      } else {
        showError(data.Error || 'Error al modificar los datos');
      }
    } catch (error) {
      showError('Error de conexión al modificar');
    }
  };

  const handleEliminar = async () => {
    if (!rnc.trim()) {
      showError('Debe buscar y seleccionar un cliente o suplidor para eliminar');
      return;
    }
    if (!window.confirm('¿Está seguro que desea eliminar este cliente/suplidor?')) {
      return;
    }

    try {
      const { ok, data } = await post('/api/clientesbackend_eliminar', {
        id_empresa: selectedCompany,
        rnc,
        tipo: tipoClienteSuplidor,
      });

      if (ok) {
        showSuccess(data.mensaje || 'Cliente/Suplidor eliminado exitosamente');
        limpiarFormulario();
      } else {
        showError(data.Error || 'Error al eliminar');
      }
    } catch (error) {
      showError('Error de conexión al eliminar');
    }
  };

  const handleBuscar = () => {
    setDescripcionBusqueda('');
    setResultadosBusqueda([]);
    setTipoBusquedaDialogo(tipoClienteSuplidor);
    setDialogoBusqueda(true);
    realizarBusqueda({ descripcion: '', tipo: tipoClienteSuplidor });
  };

  const cerrarDialogoBusqueda = () => {
    setDialogoBusqueda(false);
    setResultadosBusqueda([]);
    setDescripcionBusqueda('');
    setBuscando(false);
  };

  const realizarBusqueda = async (override = {}) => {
    const descripcion =
      override.descripcion !== undefined ? override.descripcion : descripcionBusqueda;
    const tipo = override.tipo !== undefined ? override.tipo : tipoBusquedaDialogo;
    setBuscando(true);
    try {
      const { ok, data } = await post('/api/clientesbackend_buscar', {
        id_empresa: selectedCompany,
        tipo,
        descripcion,
      });

      if (ok) {
        setResultadosBusqueda(data.resultados || []);
      } else {
        showError(data.Error || 'Error al buscar');
      }
    } catch (error) {
      showError('Error de conexión al buscar');
    } finally {
      setBuscando(false);
    }
  };

  const seleccionarCliente = async (clienteId) => {
    try {
      const { ok, data } = await post('/api/clientesbackend_obtenerporid', {
        id_empresa: selectedCompany,
        id: clienteId,
      });

      if (ok) {
        const cliente = data.cliente;

        setNombre(cliente.descrip || '');
        setDireccion(cliente.direccion || '');
        setCiudad(cliente.ciudad || '');
        setTelefono(cliente.telefono || '');
        setFax(cliente.fax || '');
        setEmail(cliente.email || '');
        setRnc(cliente.rnc || '');
        setTipoClienteSuplidor(cliente.tiposuplidor || 'Cliente');

        setRncHabilitado(false);
        setModoEdicion(true);
        setDialogoBusqueda(false);
        setClienteIdParaConfiguracion(cliente.id);
      } else {
        showError(data.Error || 'Error al obtener cliente');
      }
    } catch (error) {
      showError('Error de conexión al obtener cliente');
    }
  };

  const handleSeleccionarDesdeListado = async (clienteId) => {
    await seleccionarCliente(clienteId);
    setTabActual('registro');
  };

  const tienePermiso = tienePermisosModulo(roleDetalles, 'registroclientessuplidores');
  const tienePermisoConfiguracion = tienePermisosModulo(roleDetalles, 'configuracionclientessuplidores');

  // Botón primario reutilizable estilo FinancePro (navy lleno, sin sombra).
  const primaryBtnSx = {
    bgcolor: FP.color.navy,
    color: '#FFFFFF',
    borderRadius: 1.4,
    height: 46,
    px: 2.4,
    boxShadow: 'none',
    textTransform: 'none',
    fontWeight: 700,
    '&:hover': { bgcolor: FP.color.navyDeep, boxShadow: 'none' },
    '&.Mui-disabled': { bgcolor: '#E2E8F0', color: '#94A3B8' },
  };

  // Botón secundario / outlined (blanco con borde suave).
  const outlineBtnSx = {
    borderColor: FP.color.border,
    color: FP.color.textPrimary,
    bgcolor: '#FFFFFF',
    borderRadius: 1.4,
    height: 46,
    px: 2.4,
    textTransform: 'none',
    fontWeight: 700,
    '&:hover': { bgcolor: FP.color.surfaceAlt, borderColor: FP.color.borderStrong },
  };

  if (!tienePermiso) {
    return (
      <Box
        component="main"
        sx={{
          minHeight: '100vh',
          bgcolor: FP.color.surface,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: { xs: 2, sm: 3, md: 4 },
        }}
      >
        <Card
          elevation={0}
          sx={{
            maxWidth: 560,
            width: '100%',
            borderRadius: 2.5,
            border: `1px solid ${FP.color.border}`,
            bgcolor: FP.color.cardBg,
            boxShadow: FP.shadow.soft,
            textAlign: 'center',
            p: { xs: 3, md: 4 },
          }}
        >
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: 1.6,
              mx: 'auto',
              mb: 2.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: FP.color.dangerSoft,
              color: FP.color.danger,
            }}
          >
            <LockOutlinedIcon sx={{ fontSize: 30 }} />
          </Box>
          <Typography
            sx={{
              fontWeight: 800,
              fontSize: '1.25rem',
              color: FP.color.textPrimary,
              mb: 1,
              letterSpacing: '-0.01em',
            }}
          >
            Acceso Denegado
          </Typography>
          <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.95rem', mb: 1.2 }}>
            No tiene permisos para acceder al módulo de Registro de Clientes y Suplidores.
          </Typography>
          <Typography sx={{ color: FP.color.textMuted, fontSize: '0.85rem' }}>
            Por favor, contacte al administrador del sistema para solicitar los permisos necesarios.
          </Typography>
        </Card>
      </Box>
    );
  }

  return (
    <Box
      component="main"
      sx={{
        minHeight: '100vh',
        bgcolor: FP.color.surface,
      }}
    >
      <PageHeader
        title="Clientes y Suplidores"
        subtitle="Registro y consulta"
      />

      <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
        {/* Tabs */}
        <Card
          elevation={0}
          sx={{
            mb: 3,
            borderRadius: 2.5,
            border: `1px solid ${FP.color.border}`,
            bgcolor: FP.color.cardBg,
            boxShadow: FP.shadow.soft,
            overflow: 'hidden',
          }}
        >
          <Tabs
            value={tabActual}
            onChange={(e, newValue) => setTabActual(newValue)}
            sx={{
              minHeight: 48,
              borderBottom: `1px solid ${FP.color.border}`,
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 700,
                fontSize: '0.92rem',
                color: FP.color.textSecondary,
                minHeight: 48,
                px: 2.4,
              },
              '& .MuiTab-root.Mui-selected': { color: FP.color.navy },
              '& .MuiTabs-indicator': { backgroundColor: FP.color.navy, height: 3 },
            }}
          >
            <Tab
              value="registro"
              icon={<EditIcon sx={{ fontSize: 18 }} />}
              iconPosition="start"
              label="Registro"
            />
            <Tab
              value="listado"
              icon={<SearchIcon sx={{ fontSize: 18 }} />}
              iconPosition="start"
              label="Listado"
            />
            {tienePermisoConfiguracion && (
              <Tab
                value="configuracion"
                icon={<SettingsIcon sx={{ fontSize: 18 }} />}
                iconPosition="start"
                label="Configuración"
              />
            )}
          </Tabs>
        </Card>

        {/* Tab Registro */}
        {tabActual === 'registro' && (
          <Card
            elevation={0}
            sx={{
              borderRadius: 2.5,
              border: `1px solid ${FP.color.border}`,
              bgcolor: FP.color.cardBg,
              boxShadow: FP.shadow.soft,
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                px: { xs: 2, md: 3 },
                py: 1.6,
                borderBottom: `1px solid ${FP.color.border}`,
                bgcolor: '#FAFBFE',
                display: 'flex',
                alignItems: 'center',
                gap: 1.2,
              }}
            >
              <Box
                sx={{
                  width: 30,
                  height: 30,
                  borderRadius: 1.2,
                  bgcolor: FP.color.accentBlueSoft,
                  color: FP.color.accentBlue,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {tipoClienteSuplidor === 'Cliente' ? (
                  <PersonIcon sx={{ fontSize: 18 }} />
                ) : (
                  <BusinessIcon sx={{ fontSize: 18 }} />
                )}
              </Box>
              <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.98rem' }}>
                Datos del {tipoClienteSuplidor}
              </Typography>
              <Box sx={{ flex: 1 }} />
              <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.78rem', fontWeight: 600 }}>
                {modoEdicion ? 'Modo edición' : 'Nuevo registro'}
              </Typography>
            </Box>

            <CardContent sx={{ p: { xs: 2, md: 3 } }}>
              <Grid container spacing={2.5}>
                {/* Tipo, Nombre, Buscar */}
                <Grid item xs={12} sm={3}>
                  <FormControl fullWidth sx={fpInputSx}>
                    <InputLabel id="tipo-cs-label">Tipo</InputLabel>
                    <Select
                      labelId="tipo-cs-label"
                      value={tipoClienteSuplidor}
                      onChange={(e) => setTipoClienteSuplidor(e.target.value)}
                      label="Tipo"
                    >
                      <MenuItem value="Cliente">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <PersonIcon fontSize="small" />
                          Cliente
                        </Box>
                      </MenuItem>
                      <MenuItem value="Suplidor">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <BusinessIcon fontSize="small" />
                          Suplidor
                        </Box>
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={7}>
                  <TextField
                    fullWidth
                    label="Nombre"
                    value={nombre}
                    onChange={handleNombreChange}
                    inputRef={nombreRef}
                    required
                    autoFocus
                    InputLabelProps={{ shrink: true }}
                    placeholder="Nombre del cliente o suplidor"
                    InputProps={{
                      startAdornment: (
                        <Box sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
                          <PersonPinIcon sx={{ color: FP.color.textMuted, fontSize: 20 }} />
                        </Box>
                      ),
                    }}
                    sx={fpInputSx}
                  />
                </Grid>
                <Grid item xs={12} sm={2}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<SearchIcon />}
                    onClick={handleBuscar}
                    sx={outlineBtnSx}
                  >
                    Buscar
                  </Button>
                </Grid>

                {/* Dirección y Ciudad */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Dirección"
                    value={direccion}
                    onChange={(e) => setDireccion(e.target.value.toUpperCase())}
                    multiline
                    rows={2}
                    required
                    InputLabelProps={{ shrink: true }}
                    placeholder="Calle, sector, referencia"
                    InputProps={{
                      startAdornment: (
                        <Box sx={{ mr: 1, display: 'flex', alignItems: 'flex-start', pt: 1 }}>
                          <LocationIcon sx={{ color: FP.color.textMuted, fontSize: 20 }} />
                        </Box>
                      ),
                    }}
                    sx={{
                      ...fpInputSx,
                      '& .MuiOutlinedInput-root': {
                        ...fpInputSx['& .MuiOutlinedInput-root'],
                        height: 'auto',
                        minHeight: 46,
                        alignItems: 'flex-start',
                        py: 1,
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth sx={fpInputSx}>
                    <InputLabel id="ciudad-label">Ciudad</InputLabel>
                    <Select
                      labelId="ciudad-label"
                      value={ciudad}
                      onChange={(e) => setCiudad(e.target.value)}
                      label="Ciudad"
                    >
                      {ciudades.map((ciudadItem) => (
                        <MenuItem key={ciudadItem} value={ciudadItem}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <LocationIcon sx={{ fontSize: 18, color: FP.color.textMuted }} />
                            {ciudadItem}
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* Teléfono, Whatsapp, Email */}
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Teléfono"
                    value={telefono}
                    onChange={handleTelefonoChange}
                    placeholder="999-999-9999"
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      startAdornment: (
                        <Box sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
                          <PhoneIcon sx={{ color: FP.color.textMuted, fontSize: 20 }} />
                        </Box>
                      ),
                    }}
                    sx={fpInputSx}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Whatsapp / Celular"
                    value={fax}
                    onChange={handleFaxChange}
                    placeholder="999-999-9999"
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      startAdornment: (
                        <Box sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
                          <PhoneIcon sx={{ color: FP.color.textMuted, fontSize: 20 }} />
                        </Box>
                      ),
                    }}
                    sx={fpInputSx}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    type="email"
                    required
                    InputLabelProps={{ shrink: true }}
                    placeholder="correo@empresa.com"
                    InputProps={{
                      startAdornment: (
                        <Box sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
                          <EmailIcon sx={{ color: FP.color.textMuted, fontSize: 20 }} />
                        </Box>
                      ),
                    }}
                    sx={fpInputSx}
                  />
                </Grid>

                {/* RNC */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="RNC / Cédula"
                    value={rnc}
                    onChange={(e) => {
                      const valor = e.target.value.replace(/\D/g, '').slice(0, 12);
                      setRnc(valor);
                    }}
                    disabled={!rncHabilitado}
                    required
                    InputLabelProps={{ shrink: true }}
                    placeholder="Sólo números"
                    InputProps={{
                      startAdornment: (
                        <Box sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
                          <BadgeIcon sx={{ color: FP.color.textMuted, fontSize: 20 }} />
                        </Box>
                      ),
                    }}
                    sx={fpInputSx}
                  />
                </Grid>
              </Grid>

              {/* Botones de acción */}
              <Box
                sx={{
                  display: 'flex',
                  gap: 1.2,
                  justifyContent: 'flex-end',
                  flexWrap: 'wrap',
                  mt: 3,
                  pt: 2.5,
                  borderTop: `1px solid ${FP.color.border}`,
                }}
              >
                <Button
                  variant="outlined"
                  startIcon={<ClearIcon />}
                  onClick={limpiarFormulario}
                  sx={outlineBtnSx}
                >
                  Limpiar
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<DeleteIcon />}
                  onClick={handleEliminar}
                  disabled={!modoEdicion}
                  sx={{
                    ...outlineBtnSx,
                    borderColor: FP.color.danger,
                    color: FP.color.danger,
                    '&:hover': {
                      bgcolor: FP.color.dangerSoft,
                      borderColor: FP.color.danger,
                    },
                    '&.Mui-disabled': {
                      borderColor: FP.color.border,
                      color: FP.color.textMuted,
                    },
                  }}
                >
                  Eliminar
                </Button>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={modoEdicion ? handleModificar : handleGuardar}
                  sx={primaryBtnSx}
                >
                  {modoEdicion ? 'Modificar' : 'Guardar'}
                </Button>
              </Box>
            </CardContent>
          </Card>
        )}

        {tabActual === 'listado' && (
          <ListadoClientesSuplidores onSeleccionarCliente={handleSeleccionarDesdeListado} />
        )}

        {tienePermisoConfiguracion && tabActual === 'configuracion' && (
          <ConfiguracionClientes
            clienteId={clienteIdParaConfiguracion}
            onGuardar={() => {
              if (clienteIdParaConfiguracion) {
                seleccionarCliente(clienteIdParaConfiguracion);
              }
            }}
          />
        )}

        {/* Diálogo de Búsqueda */}
        <Dialog
          open={dialogoBusqueda}
          onClose={cerrarDialogoBusqueda}
          maxWidth="lg"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 2.5,
              border: `1px solid ${FP.color.border}`,
              boxShadow: FP.shadow.md,
              width: '100%',
              maxWidth: 1180,
              maxHeight: '90vh',
            },
          }}
        >
          <DialogTitle
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              rowGap: 1.5,
              columnGap: 2,
              px: 3,
              py: 2,
              borderBottom: `1px solid ${FP.color.border}`,
              bgcolor: '#FAFBFE',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.2,
                flex: '1 1 auto',
                minWidth: 0,
              }}
            >
              <Box
                sx={{
                  width: 30,
                  height: 30,
                  borderRadius: 1.2,
                  bgcolor: FP.color.accentBlueSoft,
                  color: FP.color.accentBlue,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <SearchIcon sx={{ fontSize: 18 }} />
              </Box>
              <Typography
                sx={{ fontWeight: 700, fontSize: '1rem', color: FP.color.textPrimary }}
                component="span"
              >
                Buscar registros
              </Typography>
              {resultadosBusqueda.length > 0 && (
                <Typography
                  sx={{
                    px: 1,
                    py: 0.25,
                    borderRadius: 1,
                    bgcolor: FP.color.accentBlueSoft,
                    color: FP.color.accentBlue,
                    fontSize: '0.72rem',
                    fontWeight: 700,
                    flexShrink: 0,
                  }}
                >
                  {resultadosBusqueda.length} resultado{resultadosBusqueda.length === 1 ? '' : 's'}
                </Typography>
              )}
            </Box>

            <ToggleButtonGroup
              exclusive
              value={tipoBusquedaDialogo}
              onChange={(_, nuevoTipo) => {
                if (nuevoTipo == null) return;
                setTipoBusquedaDialogo(nuevoTipo);
                realizarBusqueda({ tipo: nuevoTipo });
              }}
              size="small"
              sx={{
                flexShrink: 0,
                borderRadius: 1.4,
                border: `1px solid ${FP.color.border}`,
                bgcolor: '#FFFFFF',
                '& .MuiToggleButton-root': {
                  px: 1.75,
                  py: 0.85,
                  textTransform: 'none',
                  fontWeight: 700,
                  border: 'none',
                  '&.Mui-selected': {
                    bgcolor: FP.color.navy,
                    color: '#FFFFFF',
                    '&:hover': { bgcolor: FP.color.navyDeep },
                  },
                },
              }}
            >
              <ToggleButton value="Cliente" aria-label="Cliente">
                <PersonIcon sx={{ fontSize: 18, mr: 0.75 }} />
                Cliente
              </ToggleButton>
              <ToggleButton value="Suplidor" aria-label="Suplidor">
                <BusinessIcon sx={{ fontSize: 18, mr: 0.75 }} />
                Suplidor
              </ToggleButton>
            </ToggleButtonGroup>

            <IconButton
              onClick={cerrarDialogoBusqueda}
              size="small"
              sx={{
                color: FP.color.textSecondary,
                border: `1px solid ${FP.color.border}`,
                borderRadius: 1.2,
                width: 32,
                height: 32,
                flexShrink: 0,
                '&:hover': { bgcolor: FP.color.surfaceAlt, color: FP.color.navy },
              }}
            >
              <ClearIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </DialogTitle>
          <DialogContent
            sx={{
              p: 3,
              overflow: 'visible',
            }}
          >
            <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
              <Grid item xs={12} sm={9}>
                <TextField
                  fullWidth
                  label="Descripción"
                  value={descripcionBusqueda}
                  onChange={(e) => setDescripcionBusqueda(e.target.value)}
                  placeholder="Buscar por nombre…"
                  InputLabelProps={{ shrink: true }}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') realizarBusqueda();
                  }}
                  InputProps={{
                    startAdornment: (
                      <Box sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
                        <SearchIcon sx={{ color: FP.color.textMuted, fontSize: 20 }} />
                      </Box>
                    ),
                  }}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<SearchIcon />}
                  onClick={() => realizarBusqueda()}
                  disabled={buscando}
                  sx={primaryBtnSx}
                >
                  {buscando ? 'Buscando…' : 'Buscar'}
                </Button>
              </Grid>
            </Grid>

            <TableContainer
              component={Paper}
              elevation={0}
              sx={{
                borderRadius: 2,
                border: `1px solid ${FP.color.border}`,
                boxShadow: 'none',
                maxHeight: 'calc(90vh - 300px)',
                minHeight: 320,
                overflow: 'auto',
                scrollbarGutter: 'stable',
              }}
            >
              <Table
                size="small"
                sx={{
                  tableLayout: 'fixed',
                  width: '100%',
                  borderCollapse: 'separate',
                  borderSpacing: 0,
                  '& .MuiTableCell-root': { boxSizing: 'border-box' },
                }}
              >
                <colgroup>
                  {BUSQUEDA_DIALOG_COLUMNAS.map((col) => (
                    <col key={col.campo} style={{ width: col.width }} />
                  ))}
                </colgroup>
                <TableBody>
                  {buscando ? (
                    <TableRow>
                      <TableCell
                        colSpan={5}
                        align="center"
                        padding="none"
                        sx={{
                          color: FP.color.textMuted,
                          py: 4,
                          px: 2,
                          fontSize: '0.85rem',
                          boxSizing: 'border-box',
                        }}
                      >
                        Buscando…
                      </TableCell>
                    </TableRow>
                  ) : resultadosBusqueda.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={5}
                        align="center"
                        padding="none"
                        sx={{
                          color: FP.color.textMuted,
                          py: 4,
                          px: 2,
                          fontSize: '0.85rem',
                          boxSizing: 'border-box',
                        }}
                      >
                        Sin resultados. Escriba una descripción y presione Buscar.
                      </TableCell>
                    </TableRow>
                  ) : (
                    resultadosBusqueda.map((cliente) => (
                      <TableRow
                        key={cliente.id}
                        hover
                        onClick={() => seleccionarCliente(cliente.id)}
                        sx={{
                          cursor: 'pointer',
                          '&:hover': { bgcolor: FP.color.surfaceAlt },
                        }}
                      >
                        {BUSQUEDA_DIALOG_COLUMNAS.map((col) => {
                          const contenidoValor =
                            col.campo === 'id'
                              ? cliente.id
                              : col.campo === 'rnc'
                                ? cliente.rnc || '-'
                                : col.campo === 'ciudad'
                                  ? cliente.ciudad || '-'
                                  : cliente[col.campo];

                          const titleValor =
                            col.campo === 'descrip'
                              ? cliente.descrip
                              : col.campo === 'rnc'
                                ? cliente.rnc || ''
                                : col.campo === 'ciudad'
                                  ? cliente.ciudad || ''
                                  : undefined;

                          const etiquetaSx = {
                            fontSize: '0.7rem',
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            letterSpacing: '0.06em',
                            color: FP.color.textSecondary,
                            lineHeight: 1.25,
                            mb: 0.35,
                            display: 'block',
                            width: '100%',
                            minWidth: 0,
                            textAlign: col.align,
                          };

                          return col.campo === '_accion' ? (
                            <TableCell
                              key={col.campo}
                              align={col.align}
                              padding="none"
                              sx={{
                                ...busquedaDialogCeldaLayoutSx,
                                verticalAlign: 'top',
                              }}
                            >
                              <Box
                                sx={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                  gap: 0.5,
                                  width: '100%',
                                  minWidth: 0,
                                }}
                              >
                                <Typography component="span" sx={etiquetaSx}>
                                  {col.etiqueta}
                                </Typography>
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    seleccionarCliente(cliente.id);
                                  }}
                                  sx={{
                                    color: FP.color.navy,
                                    border: `1px solid ${FP.color.border}`,
                                    borderRadius: 1.2,
                                    width: 30,
                                    height: 30,
                                    '&:hover': {
                                      bgcolor: FP.color.navy,
                                      color: '#FFFFFF',
                                      borderColor: FP.color.navy,
                                    },
                                  }}
                                >
                                  <EditIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </Box>
                            </TableCell>
                          ) : (
                            <TableCell
                              key={col.campo}
                              align={col.align}
                              padding="none"
                              sx={{
                                ...busquedaDialogCeldaLayoutSx,
                                verticalAlign: 'top',
                              }}
                            >
                              <Box
                                sx={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: col.align === 'center' ? 'center' : 'stretch',
                                  width: '100%',
                                  minWidth: 0,
                                }}
                              >
                                <Typography component="span" sx={etiquetaSx}>
                                  {col.etiqueta}
                                </Typography>
                                <Typography
                                  component="span"
                                  title={titleValor}
                                  sx={{
                                    display: 'block',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    minWidth: 0,
                                    width: '100%',
                                    textAlign: col.align,
                                    color:
                                      col.campo === 'id'
                                        ? FP.color.textSecondary
                                        : FP.color.textPrimary,
                                    fontSize: col.campo === 'descrip' ? '0.9rem' : '0.85rem',
                                    fontWeight: col.campo === 'descrip' ? 600 : 400,
                                  }}
                                >
                                  {contenidoValor}
                                </Typography>
                              </Box>
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2, borderTop: `1px solid ${FP.color.border}` }}>
            <Typography sx={{ flex: 1, color: FP.color.textMuted, fontSize: '0.78rem' }}>
              Haga clic sobre una fila para seleccionar
            </Typography>
            <Button onClick={cerrarDialogoBusqueda} sx={outlineBtnSx}>
              Cerrar
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
};

export default RegistroClientes;
